<template>
    <div
        v-if="showBanner"
        class="global_banner"
        :class="{ 'global_banner--dark': dark }"
    >
        <div class="banner">
            <div v-if="!imageReady" class="banner__loader" aria-hidden="true">
                <span class="banner__spinner"></span>
            </div>
            <img
                v-if="bannerSrc"
                ref="bannerImg"
                class="banner__image"
                :class="{ 'is-ready': imageReady }"
                :src="bannerSrc"
                alt=""
                @load="onBannerLoad"
                @error="onBannerError"
            />
            <div class="degrade" aria-hidden="true"></div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import { courseCoverSrc } from '@/utils/courses';

function enrolmentTimestamp(item = {}) {
    const raw = item.dateEnrol
        || item.dateInsert
        || item.dateBuy
        || item.datePurchase
        || item.dateCreate
        || item.createdAt
        || item.course?.dateInsert
        || null;
    if (!raw) return 0;
    const time = new Date(raw).getTime();
    return Number.isFinite(time) ? time : 0;
}

function resolveCourseImage(course) {
    if (!course || typeof course !== 'object') return '';
    const nested = course.course && typeof course.course === 'object'
        ? course.course
        : course;
    // Mesma imagem dos cards: thumb (imageFinalPath2) antes da capa.
    return courseCoverSrc(nested, '') || courseCoverSrc(course, '');
}

function resolvePortalBanner(value) {
    if (!value) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'object') {
        const path = value.portalBannerFinalPath || value.portalBannerPath || value.url || '';
        return typeof path === 'string' ? path.trim() : '';
    }
    return '';
}

export default {
    props: {
        view: Boolean,
        data: {
            type: Array,
            default: () => [],
        },
        bannerPortal: {
            type: [String, Object],
            required: false,
            default: null,
        },
        hasReady: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            imageReady: false,
        };
    },
    computed: {
        ...mapState(['dark']),
        enrolledCourses() {
            if (!this.hasReady) return [];
            if (Array.isArray(this.data) && this.data.length) return this.data;
            return [];
        },
        showBanner() {
            return this.hasReady && this.enrolledCourses.length > 0;
        },
        /**
         * Último curso comprado/atrelado à conta (GET /portal/course),
         * preferindo o mais recente que tenha imageFinalPath2.
         */
        lastEnrolledCourse() {
            const list = this.enrolledCourses;
            if (!list.length) return null;

            const ranked = [...list].sort((a, b) => {
                const courseA = a?.course || a;
                const courseB = b?.course || b;
                const aHas = resolveCourseImage(courseA) ? 1 : 0;
                const bHas = resolveCourseImage(courseB) ? 1 : 0;
                if (bHas !== aHas) return bHas - aHas;
                return enrolmentTimestamp(b) - enrolmentTimestamp(a);
            });

            const newest = ranked[0];
            return newest?.course || newest || null;
        },
        bannerSrc() {
            if (!this.hasReady) return '';
            return resolvePortalBanner(this.bannerPortal)
                || resolveCourseImage(this.lastEnrolledCourse);
        },
    },
    watch: {
        bannerSrc() {
            this.imageReady = false;
            this.$nextTick(() => this.markReadyIfCached());
        },
        hasReady(ready) {
            if (!ready) this.imageReady = false;
            else if (!this.bannerSrc) this.imageReady = true;
        },
    },
    methods: {
        markReadyIfCached() {
            const img = this.$refs.bannerImg;
            if (img && img.complete && img.naturalWidth) {
                this.imageReady = true;
            }
        },
        onBannerLoad() {
            this.imageReady = true;
        },
        onBannerError() {
            this.imageReady = true;
        },
        goCourse(id) {
            window.location.href = `/aluno/curso/${id}`;
        },
    },
};
</script>

<style scoped>
.global_banner{
    position: relative;
    width: 100%;
}
.banner {
    position: relative;
    width: 100%;
    max-width: none;
    aspect-ratio: 1080 / 400;
    height: auto;
    margin: 0;
    background-color: #e8eaf0;
    border-radius: 0 0 20px 20px;
    overflow: hidden;
}

.global_banner--dark .banner {
    background-color: #1a191f;
}

.banner__image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
    position: absolute;
    inset: 0;
    opacity: 0;
    transition: opacity 0.25s ease;
}

.banner__image.is-ready {
    opacity: 1;
}

.banner__loader {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    background: inherit;
}

.banner__spinner {
    width: 48px;
    height: 48px;
    border: 5px solid #1AC7D0;
    border-bottom-color: transparent;
    border-radius: 50%;
    box-sizing: border-box;
    animation: banner-spin 1s linear infinite;
}

@keyframes banner-spin {
    to { transform: rotate(360deg); }
}

.degrade {
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    background-image: linear-gradient(
        90deg,
        rgba(15, 14, 18, 0.45) 0%,
        rgba(15, 14, 18, 0.12) 45%,
        rgba(15, 14, 18, 0) 70%
    );
}

.global_banner--dark .degrade {
    background-image: linear-gradient(
        90deg,
        rgba(15, 14, 18, 0.5) 0%,
        rgba(15, 14, 18, 0.18) 45%,
        rgba(15, 14, 18, 0) 70%
    );
}

@media (max-width: 959px) {
    .banner {
        border-radius: 0 0 12px 12px;
    }
}

.content {
    position: absolute;
    bottom: 0;
    width: 50%;
    z-index: 9;
    background-color: rgba(255, 255, 255, 0);
    top: 0%;
    display: flex;
    align-items: center;
    padding-left: 100px;
}
.content_layout {
    text-align: left;
}
.global_banner--dark .content_layout p {
    color: #F3F3F3;
    font-family: Inter;
    font-size: 48px;
    font-style: normal;
    font-weight: 700;
    line-height: 110%;
    letter-spacing: 0.96px;
}
.content_layout p {
    color: #000000;
    font-family: Inter;
    font-size: 48px;
    font-style: normal;
    font-weight: 700;
    line-height: 110%;
    letter-spacing: 0.96px;
}
.content_layout label{
    color: rgba(0, 0, 0, 0.59);
    font-family: Inter;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 103.6%; /* 16.576px */
    letter-spacing: 0.16px;
    margin-bottom: 10px;
}
.global_banner--dark .content_layout label{
    color: rgba(243, 243, 243, 0.59);
    font-family: Inter;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 103.6%; /* 16.576px */
    letter-spacing: 0.16px;
    margin-bottom: 10px;
}
button.btn-color {
    border-radius: 4px;
    background: var(--Turquesa, #1AC7D0);
    display: flex;
    padding: 12px 14px 12px 12px;
    align-items: center;
    gap: 8px;
    border: none;
    color: #ffffff;
    font-family: Inter;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 103.6%;
    letter-spacing: 0.16px;
}
.global_banner--dark button.btn-color {
    color: #000;
}
i.pi.pi-play {
    border: none;
}
@media only screen and (max-width: 600px) {
    .content {
        width: 100%;
        z-index: 9;
        background-color: rgba(255, 255, 255, 0);
        top: 0%;
        display: flex;
        align-items: center;
        padding-left: 20px;
    }
}
</style>
